class ProductVideosSlider extends HTMLElement {
  constructor() {
    super();

    this.track = this.querySelector('.product-videos__track');
    this.prevButton = this.querySelector('.product-videos__nav--prev');
    this.nextButton = this.querySelector('.product-videos__nav--next');
    this.dotsContainer = this.querySelector('.product-videos__dots');
    this.dots = this.dotsContainer ? Array.from(this.dotsContainer.children) : [];

    if (!this.track || !this.track.children.length) return;

    this.mobilePerView = Math.max(1, parseInt(this.dataset.mobilePerView, 10) || 1);
    this.desktopPerView = Math.max(1, parseInt(this.dataset.desktopPerView, 10) || 3);
    this.loopSetting = this.dataset.loop === 'true';
    this.breakpoint = 750;
    this.isAnimating = false;

    this.realSlides = Array.from(this.track.children);
    this.realCount = this.realSlides.length;
    this.cloneCount = Math.max(this.mobilePerView, this.desktopPerView);
    this.canLoop = this.loopSetting && this.realCount > this.cloneCount;

    if (this.canLoop) this.setupClones();

    this.index = this.canLoop ? this.cloneCount : 0;
    this.renderDots();
    this.setPosition(false);
    this.updateControls();

    if (this.prevButton) this.prevButton.addEventListener('click', () => this.go(-1));
    if (this.nextButton) this.nextButton.addEventListener('click', () => this.go(1));
    if (this.dotsContainer) {
      this.dotsContainer.addEventListener('click', (event) => {
        const dot = event.target.closest('.product-videos__dot');
        if (!dot) return;
        const targetIndex = parseInt(dot.dataset.index, 10);
        if (!Number.isNaN(targetIndex)) this.goTo(targetIndex);
      });
    }
    this.track.addEventListener('transitionend', this.onTransitionEnd.bind(this));

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(this.onResize.bind(this), 150);
    });

    this.setupSwipe();
  }

  currentPerView() {
    return window.innerWidth >= this.breakpoint ? this.desktopPerView : this.mobilePerView;
  }

  setupClones() {
    const lastClones = this.realSlides.slice(-this.cloneCount).map((el) => this.cloneSlide(el));
    const firstClones = this.realSlides.slice(0, this.cloneCount).map((el) => this.cloneSlide(el));

    lastClones.forEach((clone) => this.track.insertBefore(clone, this.track.firstChild));
    firstClones.forEach((clone) => this.track.appendChild(clone));
  }

  cloneSlide(el) {
    const clone = el.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('tabindex', '-1');
    clone.classList.add('product-videos__item--clone');
    Array.from(clone.attributes).forEach((attr) => {
      if (attr.name.indexOf('data-shopify') === 0) clone.removeAttribute(attr.name);
    });
    return clone;
  }

  maxIndex() {
    if (this.canLoop) return this.cloneCount + this.realCount;
    return Math.max(0, this.realCount - this.currentPerView());
  }

  go(direction) {
    if (this.isAnimating) return;

    if (this.canLoop) {
      this.index += direction;
    } else {
      const next = this.index + direction;
      if (next < 0 || next > this.maxIndex()) return;
      this.index = next;
    }

    this.isAnimating = true;
    this.setPosition(true);
    this.updateControls();
  }

  logicalIndex() {
    if (this.canLoop) {
      return ((this.index - this.cloneCount) % this.realCount + this.realCount) % this.realCount;
    }
    return this.index;
  }

  renderDots() {
    if (!this.dotsContainer) return;

    const count = this.canLoop ? this.realCount : this.maxIndex() + 1;
    if (count === this._dotsCount) return;
    this._dotsCount = count;

    this.dotsContainer.innerHTML = '';
    this.dots = [];

    if (count <= 1) return;

    for (let i = 0; i < count; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'product-videos__dot';
      dot.dataset.index = String(i);
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      this.dotsContainer.appendChild(dot);
      this.dots.push(dot);
    }
  }

  goTo(targetIndex) {
    if (this.isAnimating || targetIndex === this.logicalIndex()) return;

    this.index = this.canLoop ? this.cloneCount + targetIndex : Math.min(targetIndex, this.maxIndex());

    this.isAnimating = true;
    this.setPosition(true);
    this.updateControls();
  }

  onTransitionEnd(event) {
    if (event.target !== this.track) return;
    this.isAnimating = false;
    if (!this.canLoop) return;

    if (this.index >= this.cloneCount + this.realCount) {
      this.index = this.cloneCount;
      this.setPosition(false);
    } else if (this.index <= 0) {
      this.index = this.realCount;
      this.setPosition(false);
    }
  }

  setPosition(animate) {
    this.track.style.transition = animate ? '' : 'none';
    const perView = this.currentPerView();
    this.track.style.transform = 'translateX(' + -this.index * (100 / perView) + '%)';

    if (!animate) {
      void this.track.offsetHeight;
      this.track.style.transition = '';
    }
  }

  updateControls() {
    if (this.dots.length) {
      const active = this.logicalIndex();
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === active));
    }

    if (!this.prevButton || !this.nextButton) return;

    if (this.canLoop) {
      this.prevButton.disabled = false;
      this.nextButton.disabled = false;
      return;
    }

    const max = this.maxIndex();
    this.prevButton.disabled = this.index <= 0;
    this.nextButton.disabled = this.index >= max;
  }

  onResize() {
    if (this.isAnimating) return;
    if (!this.canLoop) {
      this.index = Math.min(this.index, this.maxIndex());
    }
    this.renderDots();
    this.setPosition(false);
    this.updateControls();
  }

  setupSwipe() {
    const threshold = 40;
    let startX = 0;
    let tracking = false;

    this.track.addEventListener(
      'touchstart',
      (event) => {
        if (this.isAnimating) return;
        startX = event.touches[0].clientX;
        tracking = true;
      },
      { passive: true }
    );

    this.track.addEventListener('touchend', (event) => {
      if (!tracking) return;
      tracking = false;
      const deltaX = event.changedTouches[0].clientX - startX;
      if (Math.abs(deltaX) < threshold) return;
      this.go(deltaX < 0 ? 1 : -1);
    });
  }
}

customElements.define('product-videos-slider', ProductVideosSlider);
